import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/cn";
import type { HealthStatus, Sentiment, Severity } from "@/types";

const badgeVariants = cva(
  "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium leading-5 whitespace-nowrap",
  {
    variants: {
      variant: {
        default: "border-transparent bg-accent text-accent-foreground",
        brand: "border-transparent bg-brand-muted text-brand",
        success: "border-transparent bg-success-muted text-success",
        warning: "border-transparent bg-warning-muted text-warning",
        danger: "border-transparent bg-danger-muted text-danger",
        info: "border-transparent bg-info-muted text-info",
        outline: "border-border text-foreground",
      },
    },
    defaultVariants: { variant: "default" },
  },
);

export interface BadgeProps extends React.HTMLAttributes<HTMLSpanElement>, VariantProps<typeof badgeVariants> {
  dot?: boolean;
}

export function Badge({ className, variant, dot, children, ...props }: BadgeProps) {
  return (
    <span className={cn(badgeVariants({ variant }), className)} {...props}>
      {dot && <span className="size-1.5 rounded-full bg-current" />}
      {children}
    </span>
  );
}

const SEVERITY_VARIANT: Record<Severity, BadgeProps["variant"]> = {
  critical: "danger",
  high: "warning",
  medium: "info",
  low: "success",
};

const SEVERITY_LABEL: Record<Severity, string> = {
  critical: "Critical",
  high: "High",
  medium: "Medium",
  low: "Low",
};

export function SeverityBadge({ severity, className }: { severity: Severity; className?: string }) {
  return (
    <Badge variant={SEVERITY_VARIANT[severity]} dot className={className}>
      {SEVERITY_LABEL[severity]}
    </Badge>
  );
}

const HEALTH_VARIANT: Record<HealthStatus, BadgeProps["variant"]> = {
  excellent: "success",
  good: "info",
  "at-risk": "warning",
  critical: "danger",
};

const HEALTH_LABEL: Record<HealthStatus, string> = {
  excellent: "Excellent",
  good: "Good",
  "at-risk": "At Risk",
  critical: "Critical",
};

export function HealthBadge({ status, className }: { status: HealthStatus; className?: string }) {
  return (
    <Badge variant={HEALTH_VARIANT[status]} dot className={className}>
      {HEALTH_LABEL[status]}
    </Badge>
  );
}

const SENTIMENT_VARIANT: Record<Sentiment, BadgeProps["variant"]> = {
  positive: "success",
  neutral: "default",
  negative: "danger",
};

export function SentimentBadge({ sentiment, className }: { sentiment: Sentiment; className?: string }) {
  return (
    <Badge variant={SENTIMENT_VARIANT[sentiment]} className={cn("capitalize", className)}>
      {sentiment}
    </Badge>
  );
}

export function StatusBadge({ status, className }: { status: string; className?: string }) {
  const normalized = status.toLowerCase();
  const variant: BadgeProps["variant"] =
    normalized === "active" || normalized === "resolved" || normalized === "approved" || normalized === "renewed" || normalized === "paid" || normalized === "done" || normalized === "completed"
      ? "success"
      : normalized === "at-risk" || normalized === "overdue" || normalized === "rejected" || normalized === "churned" || normalized === "delayed" || normalized === "lost"
        ? "danger"
        : normalized === "pending" || normalized === "onboarding" || normalized === "in-progress" || normalized === "in-negotiation" || normalized === "upcoming" || normalized === "open" || normalized === "planning"
          ? "warning"
          : "default";

  return (
    <Badge variant={variant} className={cn("capitalize", className)}>
      {status.replace(/-/g, " ")}
    </Badge>
  );
}
